import { Alert, Card } from '@cmsgov/ds-medicare-gov'
import { ContentLayout } from 'components/Layouts'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { useLegacyPremiumsHref } from 'hooks/useLegacyPremiumsHref'
import { useQueryParams } from 'hooks/useQueryParams'
import { TFunction } from 'i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { Trans } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import { BackToEasyPayBreadcrumb } from '../EasyPay/BackToEasyPayBreadcrumb'
import { Component } from '@cms-max/medicare-api-sdk'

export interface EasyPaySubmittedLayoutProps {
  t: TFunction,
  whatNextBulletComponents: Record<string, JSX.Element>,
  pageType: 'easypaysignup'
          | 'easypaylanding'
          | 'easypaysignupsuccess'
          | 'easypaystop'
          | 'easypaystopsuccess'
          | 'easypaychangebank'
          | 'easypaychangebanksuccess'
}

const NoWrapStrong: React.FC = ({ children }) => (
  <strong className='my-u-whitespace-nowrap'>{children}</strong>
)

/**
 * Creates a ContentLayout prefilled with the submitted/changed/success layout used some easy pay pages.
 * Uses the `date` query param to determine the alertDate to use in the printout.
 *
 * @param props
 * @returns
 */
export const EasyPaySubmittedLayout: React.FC<EasyPaySubmittedLayoutProps> = ({
  t,
  whatNextBulletComponents,
  pageType
}) => {
  const whatNextBullets = t('whatNext.bullets', {
    returnObjects: true
  }) as string[]
  const flags = useFlags()
  const legacyPremiumsHref = useLegacyPremiumsHref()

  const queryParams = useQueryParams()
  const d = new Date(queryParams.get('date')!)

  // If the date is missing or invalid, redirect to the Easy Pay summary page
  if (!queryParams.get('date') || !d || isNaN(d.getTime())) {
    return <Redirect to="/premiums/easy-pay" />
  }

  const alertDate = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })

  return (
    <ContentLayout
      nav={<BackToEasyPayBreadcrumb />}
      className="ds-u-padding-bottom--7"
      heading={t('heading')}
      width="wide"
      withPrintButton
      printComponent={Component.EasyPay}
    >
      <div className="ds-l-container">
        <Alert
          variation="success"
          className="ds-u-measure--wide"
          hideIcon={true}
        >
          <Trans
            t={t}
            i18nKey="alert"
            values={{ alertDate }}
            components={{
              noWrapStrong: <NoWrapStrong />
            }}
          />
        </Alert>

        <div className="ds-u-margin-top--3">
          <h2 className="ds-text-heading--xl ds-u-margin-bottom--2">
            {t('whatNext.heading')}
          </h2>
          <Card className="ds-u-padding--2 ds-u-display--inline-block">
            <ul className="ds-c-list ds-u-measure--wide">
              {whatNextBullets.map((bullet, index) => (
                <li key={`bullet-${index}`}>
                  <Trans
                    components={whatNextBulletComponents}
                  >
                    {bullet}
                  </Trans>
                </li>
              ))}
            </ul>
          </Card>

          <div>
            {flags?.useNewPremiumsPay && (
              <MyInternalLink
                to="/premiums/dashboard"
                className="ds-c-button ds-c-button--alternate ds-c-button--solid ds-u-margin-top--5"
              >
                {t('back')}
              </MyInternalLink>
            )}
            {!flags?.useNewPremiumsPay && (
              <MyLink
                href={legacyPremiumsHref}
                className="ds-c-button ds-c-button--alternate ds-c-button--solid ds-u-margin-top--5"
              >
                {t('back')}
              </MyLink>
            )}
          </div>
        </div>
      </div>
    </ContentLayout>
  )
}
